/**
 * Single source of truth for tour booking / eligibility decisions.
 *
 * IMPORTANT:
 * - UI components must not rebuild these rules themselves.
 * - A tour card, modal and /documents/:slug route must all use buildTourAccess().
 * - 1 travel point is treated as 10 validator units.
 */
export const TEAM_VALIDATORS_PER_POINT = 10;

const toNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const toPositiveNumber = (value) => Math.max(0, toNumber(value, 0));

export const toBoolean = (value) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;

  const normalized = String(value ?? "").trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes";
};

const endOfLocalDay = (rawDate) => {
  const value = String(rawDate || "").trim();
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;

  const [, year, month, day] = match;
  const date = new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    23,
    59,
    59,
    999,
  );

  return Number.isNaN(date.getTime()) ? null : date;
};

const getTourMonthStart = (tour) => {
  const value = String(tour?.tour_month_date || "").trim();
  const match = value.match(/^(\d{4})-(\d{2})/);

  if (match) {
    const [, year, month] = match;
    const date = new Date(Number(year), Number(month) - 1, 1);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  if (tour?.start_date) {
    const date = new Date(tour.start_date);
    if (!Number.isNaN(date.getTime())) {
      return new Date(date.getFullYear(), date.getMonth(), 1);
    }
  }

  return null;
};

/**
 * my-tour rows use `tourid`, while tour catalog rows use `id`.
 * Always prefer the explicit tour relation keys before falling back to id.
 */
export const getCanonicalTourId = (row) => {
  if (row === null || row === undefined) return null;
  if (typeof row === "number" || typeof row === "string") {
    const direct = Number(row);
    return Number.isFinite(direct) && direct > 0 ? direct : null;
  }

  const candidates = [
    row.tourid,
    row.tour_id,
    row.tour?.id,
    row.tour?.tourid,
    row.id,
  ];

  for (const candidate of candidates) {
    const id = Number(candidate);
    if (Number.isFinite(id) && id > 0) return id;
  }

  return null;
};

export const findTourApplication = (myTours = [], tour) => {
  const tourId = getCanonicalTourId(tour);
  if (!tourId || !Array.isArray(myTours)) return null;

  return (
    myTours.find((item) => {
      const itemTourId = Number(
        item?.id ?? item?.tour_id ?? item?.tour?.id ?? 0,
      );
      return itemTourId === tourId;
    }) || null
  );
};

export const getTourApplicationState = (
  application
) => {
  if (!application) {
    return {
      application: null,
      exists: false,

      isApplied: false,
      isRejected: false,

      transactionStatus: null,
      transactionHash: "",

      approvedCount: 0,
      passengerCount: 0,
      appliedCount: 0,
    };
  }

  const transactionStatus =
    Number(
      application?.transaction_status
    );

  const transactionHash =
    String(
      application?.transaction_hash || ""
    ).trim();

  const hasSuccessfulTransaction =
    transactionStatus === 1 &&
    transactionHash !== "";

  const rejectedCount =
    Math.max(
      0,
      Math.floor(
        toNumber(
          application?.rejected_count,
          0
        )
      )
    );

  const tourStatus =
    Number(
      application?.tour_status ??
      application?.status ??
      0
    );

  const isRejected =
    tourStatus === 2 ||
    rejectedCount > 0;

  const approvedCount =
    Math.max(
      0,
      Math.floor(
        toNumber(
          application?.approved_count,
          0
        )
      )
    );

  const passengerCount =
    Math.max(
      0,
      Math.floor(
        toNumber(
          application?.passenger_count,
          0
        )
      ) - rejectedCount
    );

  const isApplied =
    !isRejected &&
    hasSuccessfulTransaction;

  const appliedCount = approvedCount;
    // isApplied
    //   ? approvedCount
    //   : 0;

  return {
    application,

    exists: true,

    isApplied,

    isRejected,

    transactionStatus:
      Number.isFinite(
        transactionStatus
      )
        ? transactionStatus
        : null,

    transactionHash,

    approvedCount,

    passengerCount,

    appliedCount,
  };
};

// export const getTourApplicationState = (application) => {
//   if (!application) {
//     return {
//       application: null,
//       exists: false,
//       isApplied: false,
//       isRejected: false,
//       transactionStatus: null,
//       transactionHash: "",
//       appliedCount: 0,
//     };
//   }

//   const transactionStatus = Number(application?.transaction_status);
//   const transactionHash = String(application?.transaction_hash || "").trim();
//   const rejectedCount = Math.max(
//     0,
//     Math.floor(toNumber(application?.rejected_count, 0)),
//   );
//   const tourStatus = Number(application?.tour_status ?? application?.status ?? 0);

//   const isRejected = tourStatus === 2 || rejectedCount > 0;

//   // A failed transaction (status 2) is not considered an active application.
//   // The user may apply again only if the common eligibility rules allow it.
//   const isApplied = !isRejected && transactionStatus !== 2;

//   const approvedCount = Math.max(
//     0,
//     Math.floor(toNumber(application?.approved_count, 0)),
//   );
//   const passengerCount = Math.max(
//     0,
//     Math.floor(toNumber(application?.passenger_count, 0)) - rejectedCount,
//   );

//   // Conservative capacity accounting: never allow the UI to allocate fewer
//   // already-used seats than the server row indicates.
//   const appliedCount = isApplied ? Math.max(approvedCount, passengerCount) : 0;

//     console.log("transactionStatus" ,transactionStatus);
//   console.log("transactionHash" ,transactionHash);
//   console.log("rejectedCount" ,rejectedCount);
//   console.log("tourStatus" ,tourStatus);
//   console.log("isRejected" ,isRejected);
//   console.log("isApplied" ,isApplied);
//   console.log("approvedCount" ,approvedCount);
//   console.log("passengerCount" ,passengerCount);
//   console.log("appliedCount" ,appliedCount);

//   return {
//     application,
//     exists: true,
//     isApplied,
//     isRejected,
//     transactionStatus: Number.isFinite(transactionStatus)
//       ? transactionStatus
//       : null,
//     transactionHash,
//     appliedCount,
//   };
// };

export const calculateTourEligibility = ({
  requiredPoints,
  requiredTeamValidators,
  availablePoints,
  availableTeamValidators,
  applicationOpen,
  appliedCount = 0,
}) => {
  const pointsRequired =
    toPositiveNumber(
      requiredPoints
    );

  const validatorsRequired =
    toPositiveNumber(
      requiredTeamValidators
    );

  const pointsAvailable =
    toPositiveNumber(
      availablePoints
    );

  const validatorsAvailable =
    toPositiveNumber(
      availableTeamValidators
    );

  const alreadyApplied =
    Math.max(
      0,
      Math.floor(
        toNumber(
          appliedCount,
          0
        )
      )
    );

  const pointsAvailableUnits =
    Math.floor(
      pointsAvailable *
        TEAM_VALIDATORS_PER_POINT
    );

  const validatorAvailableUnits =
    Math.floor(
      validatorsAvailable
    );

  const totalAvailableUnits =
    pointsAvailableUnits +
    validatorAvailableUnits;

  const requiredUnitsFromPoints =
    pointsRequired > 0
      ? Math.ceil(
          pointsRequired *
            TEAM_VALIDATORS_PER_POINT
        )
      : 0;

  const requiredUnitsFromValidators =
    validatorsRequired > 0
      ? Math.ceil(
          validatorsRequired
        )
      : 0;

  const requiredUnits =
    Math.max(
      requiredUnitsFromPoints,
      requiredUnitsFromValidators
    );

  const additionalPassengers =
    requiredUnits > 0
      ? Math.floor(
          totalAvailableUnits /
            requiredUnits
        )
      : 0;

  const maxPassengers =
    alreadyApplied +
    additionalPassengers;

  const remainingPassengers =
    additionalPassengers;

  const applicationIsOpen =
    toBoolean(
      applicationOpen
    );

  const qualifiedByEquivalentBalance =
    requiredUnits > 0 &&
    additionalPassengers > 0;

  const isEligible =
    applicationIsOpen &&
    qualifiedByEquivalentBalance &&
    remainingPassengers > 0;

  let reason = "";

  if (!applicationIsOpen) {
    reason =
      "Applications are not open for this tour.";
  } else if (
    requiredUnits <= 0
  ) {
    reason =
      "Tour eligibility requirement is not configured.";
  } else if (
    additionalPassengers <= 0
  ) {
    reason =
      "Not enough remaining travel points or direct team validators.";
  }

  return {
    TEAM_VALIDATORS_PER_POINT,
    pointsRequired,
    validatorsRequired,
    pointsAvailable,
    validatorsAvailable,
    pointsAvailableUnits,
    validatorAvailableUnits,
    totalAvailableUnits,
    requiredUnits,
    requiredUnitsFromPoints,
    requiredUnitsFromValidators,
    totalEquivalentPoints:
      totalAvailableUnits /
      TEAM_VALIDATORS_PER_POINT,
    requiredEquivalentPoints:
      requiredUnits /
      TEAM_VALIDATORS_PER_POINT,
    maxPassengers,
    alreadyApplied,
    additionalPassengers,
    remainingPassengers,
    qualifiedByEquivalentBalance,
    applicationOpen:
      applicationIsOpen,
    isEligible,
    reason,
  };
};

// export const calculateTourEligibility = ({
//   requiredPoints,
//   requiredTeamValidators,
//   availablePoints,
//   availableTeamValidators,
//   applicationOpen,
//   appliedCount = 0,
// }) => {
//   const pointsRequired = toPositiveNumber(requiredPoints);
//   const validatorsRequired = toPositiveNumber(requiredTeamValidators);
//   const pointsAvailable = toPositiveNumber(availablePoints);
//   const validatorsAvailable = toPositiveNumber(availableTeamValidators);
//   const alreadyApplied = Math.max(0, Math.floor(toNumber(appliedCount, 0)));

//   const pointsAvailableUnits = Math.floor(
//     pointsAvailable * TEAM_VALIDATORS_PER_POINT,
//   );
//   const validatorAvailableUnits = Math.floor(validatorsAvailable);
//   const totalAvailableUnits = pointsAvailableUnits + validatorAvailableUnits;

//   const requiredUnitsFromPoints =
//     pointsRequired > 0
//       ? Math.ceil(pointsRequired * TEAM_VALIDATORS_PER_POINT)
//       : 0;
//   const requiredUnitsFromValidators =
//     validatorsRequired > 0 ? Math.ceil(validatorsRequired) : 0;

//   // Both backend fields describe the same eligibility threshold in different
//   // units. If they disagree, use the stricter threshold. Do NOT add them.
//   const requiredUnits = Math.max(
//     requiredUnitsFromPoints,
//     requiredUnitsFromValidators,
//   );

//   const maxPassengers =
//     requiredUnits > 0 ? Math.floor(totalAvailableUnits / requiredUnits) : 0;
//   const remainingPassengers = Math.max(maxPassengers - alreadyApplied, 0);

//   const applicationIsOpen = toBoolean(applicationOpen);
//   const qualifiedByEquivalentBalance =
//     requiredUnits > 0 && totalAvailableUnits >= requiredUnits;

//   const isEligible =
//     applicationIsOpen &&
//     qualifiedByEquivalentBalance &&
//     remainingPassengers > 0;

//   let reason = "";
//   if (!applicationIsOpen) {
//     reason = "Applications are not open for this tour.";
//   } else if (requiredUnits <= 0) {
//     reason = "Tour eligibility requirement is not configured.";
//   } else if (!qualifiedByEquivalentBalance) {
//     reason = "Not enough travel points or direct team validators.";
//   } else if (remainingPassengers <= 0) {
//     reason = "Passenger limit reached.";
//   }

//   return {
//     TEAM_VALIDATORS_PER_POINT,
//     pointsRequired,
//     validatorsRequired,
//     pointsAvailable,
//     validatorsAvailable,
//     pointsAvailableUnits,
//     validatorAvailableUnits,
//     totalAvailableUnits,
//     requiredUnits,
//     requiredUnitsFromPoints,
//     requiredUnitsFromValidators,
//     totalEquivalentPoints: totalAvailableUnits / TEAM_VALIDATORS_PER_POINT,
//     requiredEquivalentPoints: requiredUnits / TEAM_VALIDATORS_PER_POINT,
//     maxPassengers,
//     alreadyApplied,
//     remainingPassengers,
//     qualifiedByEquivalentBalance,
//     applicationOpen: applicationIsOpen,
//     isEligible,
//     reason,
//   };
// };

export const isTourDateInFuture = (tour) => {
  const tourMonthStart = getTourMonthStart(tour);
  if (!tourMonthStart) return false;

  const now = new Date();
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  return tourMonthStart.getTime() > currentMonthStart.getTime();
};

export const getTourBookingState = (tour, nowValue = new Date()) => {
  const now = nowValue instanceof Date ? nowValue : new Date(nowValue);
  const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const tourMonthStart = getTourMonthStart(tour);

  const futureTour =
    !!tourMonthStart && tourMonthStart.getTime() > currentMonthStart.getTime();
  const currentTourMonth =
    !!tourMonthStart && tourMonthStart.getTime() === currentMonthStart.getTime();
  const isCompleted =
    !!tourMonthStart && tourMonthStart.getTime() < currentMonthStart.getTime();

  const bookingStartStatus = Number(tour?.booking_start_status || 0);
  const deadline = endOfLocalDay(tour?.last_application_date);
  const bookingDeadlinePassed = !!deadline && deadline.getTime() < now.getTime();

  const bookingNotStarted = bookingStartStatus !== 1 && !isCompleted;
  const bookingStarted =
    bookingStartStatus === 1 && !isCompleted && !bookingDeadlinePassed;
  const bookingClosed =
    !isCompleted && bookingStartStatus === 1 && bookingDeadlinePassed;

  // Upcoming means booking has not started. A closed booking is NOT upcoming.
  const isUpcoming = bookingNotStarted;

  return {
    bookingStartStatus,
    bookingStarted,
    bookingNotStarted,
    bookingClosed,
    bookingDeadlinePassed,
    futureTour,
    currentTourMonth,
    isUpcoming,
    isCompleted,
    deadline,
  };
};

/**
 * Canonical decision consumed by tour cards, modals and document route.
 */
export const buildTourAccess = ({ tour, myTours = [] } = {}) => {
  if (!tour) {
    return {
      canOpenDocuments: false,
      canAddPassenger: false,
      canApply: false,
      canContinueApplication: false,
      status: "unavailable",
      statusLabel: "Unavailable",
      reason: "Tour was not found.",
      eligibility: calculateTourEligibility({}),
      booking: getTourBookingState(null),
      application: getTourApplicationState(null),
    };
  }

  const booking = getTourBookingState(tour);
  const applicationRow = findTourApplication(myTours, tour);
  const application = getTourApplicationState(applicationRow);
  const serverEligibility = tour?.eligibility || {};

  const backendApplicationOpen = toBoolean(serverEligibility?.application_open);
  const applicationOpen = booking.bookingStarted && backendApplicationOpen;

  const eligibility = calculateTourEligibility({
    requiredPoints: tour?.required_points,
    requiredTeamValidators: tour?.direct_team_validators,
    availablePoints: serverEligibility?.available_points,
    availableTeamValidators: serverEligibility?.direct_team_validators,
    applicationOpen,
    appliedCount: application.appliedCount,
  });

  const canApply =
    !application.isRejected &&
    !application.isApplied &&
    booking.bookingStarted &&
    eligibility.isEligible;

  const canContinueApplication =
    application.isApplied &&
    !application.isRejected &&
    booking.bookingStarted &&
    eligibility.applicationOpen;

  const canOpenDocuments = canApply || canContinueApplication;
  const canAddPassenger =
    canOpenDocuments && eligibility.remainingPassengers > 0;

  let status = "not-eligible";
  let statusLabel = "Not Eligible";
  let reason = eligibility.reason;

  if (booking.isCompleted) {
    status = "completed";
    statusLabel = "Completed";
    reason = "This tour has been completed.";
  } else if (booking.bookingClosed) {
    status = "closed";
    statusLabel = "Booking Closed";
    reason = "The booking deadline has passed.";
  } else if (application.isRejected) {
    status = "rejected";
    statusLabel = "Rejected";
    reason = "Your application for this tour was rejected.";
  } else if (application.isApplied) {
    status = "applied";
    statusLabel = "Applied";
    reason = canContinueApplication
      ? "You can continue managing this booking."
      : "This booking is currently not editable.";
  } else if (booking.isUpcoming) {
    status = "upcoming";
    statusLabel = "Upcoming";
    reason = "Booking has not started yet.";
  } else if (canApply) {
    status = "eligible";
    statusLabel = "Eligible";
    reason = "You are eligible to apply for this tour.";
  }

  return {
    tourId: getCanonicalTourId(tour),
    booking,
    application,
    eligibility,
    backendApplicationOpen,
    applicationOpen,
    isEligible: canApply,
    isApplied: application.isApplied,
    isRejected: application.isRejected,
    canApply,
    canContinueApplication,
    canOpenDocuments,
    canAddPassenger,
    status,
    statusLabel,
    reason,
  };
};

export const decorateTour = (tour, myTours = []) => {
  const access = buildTourAccess({ tour, myTours });

  return {
    ...tour,
    access,
    bookingStarted: access.booking.bookingStarted,
    isUpcoming: access.booking.isUpcoming,
    isCompleted: access.booking.isCompleted,
    isEligible: access.isEligible,
    isApplied: access.isApplied,
    isRejected: access.isRejected,
    maxTickets: access.eligibility.maxPassengers,
    alreadyApplied: access.eligibility.alreadyApplied,
    moreTicketsCanBuy: access.eligibility.remainingPassengers,
    applicationOpen: access.applicationOpen,
    eligibilityDetails: access.eligibility,
  };
};

const tourTime = (tour) => {
  if (tour?.start_date) {
    const date = new Date(tour.start_date);
    if (!Number.isNaN(date.getTime())) return date.getTime();
  }

  const month = getTourMonthStart(tour);
  return month ? month.getTime() : 0;
};

export const sortToursForDashboard = (tours = []) => {
  return [...tours].sort((a, b) => {
    const aAccess = a?.access || buildTourAccess({ tour: a });
    const bAccess = b?.access || buildTourAccess({ tour: b });

    const priority = (access) => {
      if (access.booking.bookingStarted) return 1;
      if (access.booking.isUpcoming) return 2;
      if (access.booking.bookingClosed) return 3;
      if (access.booking.isCompleted) return 4;
      return 5;
    };

    const priorityDiff = priority(aAccess) - priority(bAccess);
    if (priorityDiff !== 0) return priorityDiff;

    const aTime = tourTime(a);
    const bTime = tourTime(b);

    if (aAccess.booking.isCompleted && bAccess.booking.isCompleted) {
      return bTime - aTime;
    }

    return aTime - bTime;
  });
};

/**
 * One redemption requirement per passenger, using exactly the same unit model
 * as calculateTourEligibility().
 */
export const calculateRedemption = ({ tour, passengerCount = 0 } = {}) => {
  const eligibility = calculateTourEligibility({
    requiredPoints: tour?.required_points,
    requiredTeamValidators: tour?.direct_team_validators,
    availablePoints: tour?.eligibility?.available_points,
    availableTeamValidators: tour?.eligibility?.direct_team_validators,
    applicationOpen: true,
    appliedCount: 0,
  });

  const count = Math.max(0, Math.floor(toNumber(passengerCount, 0)));
  const totalRedeemUnits = eligibility.requiredUnits * count;

  return {
    ...eligibility,
    passengerCount: count,
    totalRedeemUnits,
    totalPointsToRedeem: totalRedeemUnits / TEAM_VALIDATORS_PER_POINT,
    availableRedeemUnits: eligibility.totalAvailableUnits,
    availableEquivalentPoints: eligibility.totalEquivalentPoints,
    hasEnoughRedeemBalance:
      totalRedeemUnits > 0 && eligibility.totalAvailableUnits >= totalRedeemUnits,
  };
};
